import { Box, Alert, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import UserDetailsPanel from '../../../users/components/UserDetailsPanel';
import { getOrganisationUserDetails } from '../../../users/api/userManagementApi';

interface UserDrawerContentProps {
  userId: string;
}

export function UserDrawerContent({ userId }: UserDrawerContentProps) {
  // Defensive check for missing userId
  const isValidUserId = Boolean(userId && userId.trim() !== '');

  const {
    data: response,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getOrganisationUserDetails(userId),
    enabled: isValidUserId,
  });

  const user = response?.user ?? null;

  // Show message if no userId provided
  if (!isValidUserId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No user selected</Alert>
      </Box>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load user details'}
        </Alert>
      </Box>
    );
  }

  // Show message if no user data returned
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No user details available</Alert>
      </Box>
    );
  }

  return <UserDetailsPanel user={user} loading={false} />;
}
